/**
 * Call Center Settings Page
 * Manages call scripts, objection library, knowledge base, and system configuration
 */

import React, { useState, useEffect } from 'react';
import { AdminPageLayout } from '@/components/layout/AdminPageLayout';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/hooks/useAuth';

// React Query hooks
import { useCallScripts, useCreateCallScript, useUpdateCallScript, useDeleteCallScript } from '@/lib/hooks/useCallScripts';
import { useObjectionResponses, useCreateObjectionResponse, useUpdateObjectionResponse, useDeleteObjectionResponse } from '@/lib/hooks/useObjectionResponses';
import { useKnowledgeArticles, useCreateKnowledgeArticle, useUpdateKnowledgeArticle, useDeleteKnowledgeArticle } from '@/lib/hooks/useKnowledgeArticles';
import { useTwilioConfig, useUpsertTwilioConfig } from '@/lib/hooks/useTwilioConfig';
import { useSystemConfig, useUpsertSystemConfig } from '@/lib/hooks/useSystemConfig';

// CRUD modals
import { CallScriptModal } from '@/components/call-center-settings/CallScriptModal';
import { ObjectionModal } from '@/components/call-center-settings/ObjectionModal';
import { KnowledgeArticleModal } from '@/components/call-center-settings/KnowledgeArticleModal';

import type { CallScript, ObjectionResponse, KnowledgeArticle, TwilioConfig, SystemConfig } from '@/lib/types/call-center-settings';

type SettingTab = 'scripts' | 'objections' | 'knowledge' | 'twilio' | 'system';

export default function CallCenterSettings() {
  const { theme } = useTheme();
  const { user } = useAuth();
  // Get company_id from user metadata or default to empty string
  const companyId = (user?.user_metadata?.company_id as string) || '';

  const [activeTab, setActiveTab] = useState<SettingTab>('scripts');
  
  // Modal states
  const [scriptModalOpen, setScriptModalOpen] = useState(false);
  const [objectionModalOpen, setObjectionModalOpen] = useState(false);
  const [articleModalOpen, setArticleModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  
  // Notification state
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Auto-dismiss notifications after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Data fetching
  const { data: scripts = [], isLoading: scriptsLoading } = useCallScripts(companyId);
  const { data: objections = [], isLoading: objectionsLoading } = useObjectionResponses(companyId);
  const { data: articles = [], isLoading: articlesLoading } = useKnowledgeArticles(companyId);
  const { data: twilioConfig, isLoading: twilioLoading } = useTwilioConfig(companyId);
  const { data: systemConfig, isLoading: systemLoading } = useSystemConfig(companyId);

  // Mutations
  const createScript = useCreateCallScript();
  const updateScript = useUpdateCallScript();
  const deleteScript = useDeleteCallScript();
  const createObjection = useCreateObjectionResponse();
  const updateObjection = useUpdateObjectionResponse();
  const deleteObjection = useDeleteObjectionResponse();
  const createArticle = useCreateKnowledgeArticle();
  const updateArticle = useUpdateKnowledgeArticle();
  const deleteArticle = useDeleteKnowledgeArticle();
  const updateTwilioConfig = useUpsertTwilioConfig();
  const updateSystemConfig = useUpsertSystemConfig();

  // Handlers
  const handleSaveScript = async (data: Partial<CallScript>) => {
    try {
      if (editingItem?.id) {
        const { company_id, id, created_at, updated_at, ...updateData } = data;
        await updateScript.mutateAsync({ id: editingItem.id, data: updateData as any });
        setNotification({ type: 'success', message: 'Call script updated successfully' });
      } else {
        await createScript.mutateAsync(data as any);
        setNotification({ type: 'success', message: 'Call script created successfully' });
      }
      setScriptModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      setNotification({ type: 'error', message: `Failed to save call script: ${error instanceof Error ? error.message : 'Unknown error'}` });
      throw error;
    }
  };

  const handleSaveObjection = async (data: Partial<ObjectionResponse>) => {
    try {
      if (editingItem?.id) {
        const { company_id, id, created_at, updated_at, ...updateData } = data;
        await updateObjection.mutateAsync({ id: editingItem.id, data: updateData as any });
        setNotification({ type: 'success', message: 'Objection response updated successfully' });
      } else {
        await createObjection.mutateAsync(data as any);
        setNotification({ type: 'success', message: 'Objection response created successfully' });
      }
      setObjectionModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      setNotification({ type: 'error', message: `Failed to save objection response: ${error instanceof Error ? error.message : 'Unknown error'}` });
      throw error;
    }
  };

  const handleSaveArticle = async (data: Partial<KnowledgeArticle>) => {
    try {
      if (editingItem?.id) {
        const { company_id, id, created_at, updated_at, ...updateData } = data;
        await updateArticle.mutateAsync({ id: editingItem.id, data: updateData as any });
        setNotification({ type: 'success', message: 'Knowledge article updated successfully' });
      } else {
        await createArticle.mutateAsync(data as any);
        setNotification({ type: 'success', message: 'Knowledge article created successfully' });
      }
      setArticleModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      setNotification({ type: 'error', message: `Failed to save knowledge article: ${error instanceof Error ? error.message : 'Unknown error'}` });
      throw error;
    }
  };

  const handleSaveTwilioConfig = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const formData = new FormData(e.currentTarget);
      const authToken = formData.get('auth_token') as string;
      const input = {
        account_sid: formData.get('account_sid') as string,
        auth_token_encrypted: authToken || undefined,
        main_phone_number: (formData.get('phone_number') as string) || undefined,
        webhook_url: (formData.get('webhook_url') as string) || undefined,
        recording_enabled: formData.get('recording_enabled') === 'on',
        transcription_enabled: formData.get('transcription_enabled') === 'on',
        transcription_provider: (formData.get('transcription_provider') as any) || undefined,
      };
      await updateTwilioConfig.mutateAsync({ companyId, input });
      setNotification({ type: 'success', message: 'Twilio configuration saved successfully' });
    } catch (error) {
      setNotification({ type: 'error', message: `Failed to save Twilio configuration: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
  };

  const handleSaveSystemConfig = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const formData = new FormData(e.currentTarget);
      const input = {
        auto_call_distribution: formData.get('auto_call_distribution') === 'on',
        ring_strategy: formData.get('ring_strategy') as any,
        max_ring_time: parseInt(formData.get('max_ring_time') as string) || 30,
        voicemail_enabled: formData.get('voicemail_enabled') === 'on',
        callback_enabled: formData.get('callback_enabled') === 'on',
        quality_monitoring: formData.get('quality_monitoring') === 'on',
        ai_assistance: formData.get('ai_assistance') === 'on',
        real_time_transcription: formData.get('real_time_transcription') === 'on',
        sentiment_analysis: formData.get('sentiment_analysis') === 'on',
      } as any;
      await updateSystemConfig.mutateAsync({ companyId, input });
      setNotification({ type: 'success', message: 'System configuration saved successfully' });
    } catch (error) {
      setNotification({ type: 'error', message: `Failed to save system configuration: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
  };

  const tabs = [
    { id: 'scripts' as SettingTab, label: 'Call Scripts', icon: '📝' },
    { id: 'objections' as SettingTab, label: 'Objections', icon: '💬' },
    { id: 'knowledge' as SettingTab, label: 'Knowledge Base', icon: '📚' },
    { id: 'twilio' as SettingTab, label: 'Twilio', icon: '☎️' },
    { id: 'system' as SettingTab, label: 'System', icon: '⚙️' }
  ];

  const isLoading = scriptsLoading || objectionsLoading || articlesLoading || twilioLoading || systemLoading;

  const handleDeleteScript = async (id: string) => {
    try {
      await deleteScript.mutateAsync(id);
      setNotification({ type: 'success', message: 'Call script deleted successfully' });
    } catch (error) {
      setNotification({ type: 'error', message: `Failed to delete call script: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
  };

  const handleDeleteObjection = async (id: string) => {
    try {
      await deleteObjection.mutateAsync(id);
      setNotification({ type: 'success', message: 'Objection response deleted successfully' });
    } catch (error) {
      setNotification({ type: 'error', message: `Failed to delete objection response: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
  };

  const handleDeleteArticle = async (id: string) => {
    try {
      await deleteArticle.mutateAsync(id);
      setNotification({ type: 'success', message: 'Knowledge article deleted successfully' });
    } catch (error) {
      setNotification({ type: 'error', message: `Failed to delete knowledge article: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
  };

  return (
    <AdminPageLayout title="Call Center Settings" subtitle="Configure call center operations">
      {/* Notification Banner */}
      {notification && (
        <div className="mb-4">
          <Alert variant={notification.type === 'error' ? 'destructive' : 'default'}>
            <AlertDescription>
              {notification.message}
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Modals */}
      <CallScriptModal
        isOpen={scriptModalOpen}
        onClose={() => { setScriptModalOpen(false); setEditingItem(null); }}
        onSave={handleSaveScript}
        script={editingItem}
        companyId={companyId}
      />
      <ObjectionModal
        isOpen={objectionModalOpen}
        onClose={() => { setObjectionModalOpen(false); setEditingItem(null); }}
        onSave={handleSaveObjection}
        objection={editingItem}
        companyId={companyId}
      />
      <KnowledgeArticleModal
        isOpen={articleModalOpen}
        onClose={() => { setArticleModalOpen(false); setEditingItem(null); }}
        onSave={handleSaveArticle}
        article={editingItem}
        companyId={companyId}
      />

      {/* Tabs */}
      <Card className="p-0" style={{ backgroundColor: theme.colors.bgCard, borderColor: theme.colors.borderColor }}>
        <div style={{ borderBottom: `1px solid ${theme.colors.borderColor}` }}>
          <nav className="flex -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors"
                style={{
                  borderBottomColor: activeTab === tab.id ? theme.colors.primaryColor : 'transparent',
                  color: activeTab === tab.id ? theme.colors.primaryColor : theme.colors.textSecondary
                }}
              >
                <span className="text-lg">{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {isLoading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: theme.colors.primaryColor }}></div>
            </div>
          )}

          {/* Scripts Tab */}
          {!isLoading && activeTab === 'scripts' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold" style={{ color: theme.colors.textPrimary }}>Call Scripts</h2>
                <button 
                  onClick={() => { setEditingItem(null); setScriptModalOpen(true); }}
                  className="px-4 py-2 rounded"
                  style={{ backgroundColor: theme.colors.primaryColor, color: '#ffffff' }}
                >
                  + Add Script
                </button>
              </div>
              {scripts.length === 0 ? (
                <p style={{ color: theme.colors.textSecondary }}>No scripts yet.</p>
              ) : (
                scripts.map((script) => (
                  <div key={script.id} className="p-4 rounded" style={{ backgroundColor: theme.colors.bgHover }}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold" style={{ color: theme.colors.textPrimary }}>{script.name}</h3>
                        <p className="text-sm" style={{ color: theme.colors.textSecondary }}>{script.script_type}</p>
                      </div>
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => { setEditingItem(script); setScriptModalOpen(true); }}
                          className="px-3 py-1 rounded text-sm"
                          style={{ backgroundColor: theme.colors.primaryColor, color: '#ffffff' }}
                        >
                          Edit
                        </button>
                        <button 
                          onClick={async () => { 
                            if (confirm('Delete this script?')) await handleDeleteScript(script.id);
                          }}
                          className="px-3 py-1 rounded text-sm"
                          style={{ backgroundColor: theme.colors.dangerBg, color: theme.colors.dangerText }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Objections Tab */}
          {!isLoading && activeTab === 'objections' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold" style={{ color: theme.colors.textPrimary }}>Objection Responses</h2>
                <button 
                  onClick={() => { setEditingItem(null); setObjectionModalOpen(true); }}
                  className="px-4 py-2 rounded"
                  style={{ backgroundColor: theme.colors.primaryColor, color: '#ffffff' }}
                >
                  + Add Objection
                </button>
              </div>
              {objections.length === 0 ? (
                <p style={{ color: theme.colors.textSecondary }}>No objections yet.</p>
              ) : (
                objections.map((objection) => (
                  <div key={objection.id} className="p-4 rounded" style={{ backgroundColor: theme.colors.bgHover }}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold" style={{ color: theme.colors.textPrimary }}>{objection.objection_text}</h3>
                        <p className="text-sm" style={{ color: theme.colors.textSecondary }}>{objection.category}</p>
                      </div>
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => { setEditingItem(objection); setObjectionModalOpen(true); }}
                          className="px-3 py-1 rounded text-sm"
                          style={{ backgroundColor: theme.colors.primaryColor, color: '#ffffff' }}
                        >
                          Edit
                        </button>
                        <button 
                          onClick={async () => { 
                            if (confirm('Delete this objection?')) await handleDeleteObjection(objection.id);
                          }}
                          className="px-3 py-1 rounded text-sm"
                          style={{ backgroundColor: theme.colors.dangerBg, color: theme.colors.dangerText }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Knowledge Tab */}
          {!isLoading && activeTab === 'knowledge' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold" style={{ color: theme.colors.textPrimary }}>Knowledge Articles</h2>
                <button 
                  onClick={() => { setEditingItem(null); setArticleModalOpen(true); }}
                  className="px-4 py-2 rounded"
                  style={{ backgroundColor: theme.colors.primaryColor, color: '#ffffff' }}
                >
                  + Add Article
                </button>
              </div>
              {articles.length === 0 ? (
                <p style={{ color: theme.colors.textSecondary }}>No articles yet.</p>
              ) : (
                articles.map((article) => (
                  <div key={article.id} className="p-4 rounded" style={{ backgroundColor: theme.colors.bgHover }}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold" style={{ color: theme.colors.textPrimary }}>{article.title}</h3>
                        <p className="text-sm" style={{ color: theme.colors.textSecondary }}>{article.category}</p>
                      </div>
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => { setEditingItem(article); setArticleModalOpen(true); }}
                          className="px-3 py-1 rounded text-sm"
                          style={{ backgroundColor: theme.colors.primaryColor, color: '#ffffff' }}
                        >
                          Edit
                        </button>
                        <button 
                          onClick={async () => { 
                            if (confirm('Delete this article?')) await handleDeleteArticle(article.id);
                          }}
                          className="px-3 py-1 rounded text-sm"
                          style={{ backgroundColor: theme.colors.dangerBg, color: theme.colors.dangerText }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Twilio Tab */}
          {!isLoading && activeTab === 'twilio' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>Twilio Configuration</h2>
              <form onSubmit={handleSaveTwilioConfig} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                      Account SID *
                    </label>
                    <input
                      type="text"
                      name="account_sid"
                      required
                      defaultValue={twilioConfig?.account_sid || ''}
                      className="w-full px-3 py-2 rounded border"
                      style={{
                        backgroundColor: theme.colors.bgPage,
                        borderColor: theme.colors.borderColor,
                        color: theme.colors.textPrimary
                      }}
                      placeholder="AC..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                      Auth Token (leave blank to keep existing)
                    </label>
                    <input
                      type="password"
                      name="auth_token"
                      className="w-full px-3 py-2 rounded border"
                      style={{
                        backgroundColor: theme.colors.bgPage,
                        borderColor: theme.colors.borderColor,
                        color: theme.colors.textPrimary
                      }}
                      placeholder="••••••••"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                      Main Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone_number"
                      defaultValue={twilioConfig?.main_phone_number || ''}
                      className="w-full px-3 py-2 rounded border"
                      style={{
                        backgroundColor: theme.colors.bgPage,
                        borderColor: theme.colors.borderColor,
                        color: theme.colors.textPrimary
                      }}
                      placeholder="+1234567890"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                      Webhook URL
                    </label>
                    <input
                      type="url"
                      name="webhook_url"
                      defaultValue={twilioConfig?.webhook_url || ''}
                      className="w-full px-3 py-2 rounded border"
                      style={{
                        backgroundColor: theme.colors.bgPage,
                        borderColor: theme.colors.borderColor,
                        color: theme.colors.textPrimary
                      }}
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div className="space-y-4 p-4 rounded" style={{ backgroundColor: theme.colors.bgHover }}>
                  <h3 className="font-semibold" style={{ color: theme.colors.textPrimary }}>Feature Toggles</h3>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="recording_enabled"
                      name="recording_enabled"
                      defaultChecked={twilioConfig?.recording_enabled}
                      className="mr-2"
                    />
                    <label htmlFor="recording_enabled" style={{ color: theme.colors.textPrimary }}>
                      Enable Call Recording
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="transcription_enabled"
                      name="transcription_enabled"
                      defaultChecked={twilioConfig?.transcription_enabled}
                      className="mr-2"
                    />
                    <label htmlFor="transcription_enabled" style={{ color: theme.colors.textPrimary }}>
                      Enable Transcription
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                      Transcription Provider
                    </label>
                    <select
                      name="transcription_provider"
                      defaultValue={twilioConfig?.transcription_provider || ''}
                      className="w-full px-3 py-2 rounded border"
                      style={{
                        backgroundColor: theme.colors.bgPage,
                        borderColor: theme.colors.borderColor,
                        color: theme.colors.textPrimary
                      }}
                    >
                      <option value="">None</option>
                      <option value="deepgram">Deepgram</option>
                      <option value="openai">OpenAI</option>
                      <option value="google">Google</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={updateTwilioConfig.isPending}
                    className="px-6 py-2 rounded font-medium"
                    style={{
                      backgroundColor: theme.colors.primaryColor,
                      color: '#ffffff'
                    }}
                  >
                    {updateTwilioConfig.isPending ? 'Saving...' : 'Save Configuration'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* System Tab */}
          {!isLoading && activeTab === 'system' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>System Configuration</h2>
              <form onSubmit={handleSaveSystemConfig} className="space-y-6">
                <div className="space-y-4 p-4 rounded" style={{ backgroundColor: theme.colors.bgHover }}>
                  <h3 className="font-semibold" style={{ color: theme.colors.textPrimary }}>Call Distribution</h3>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="auto_call_distribution"
                      name="auto_call_distribution"
                      defaultChecked={(systemConfig as any)?.auto_call_distribution}
                      className="mr-2"
                    />
                    <label htmlFor="auto_call_distribution" style={{ color: theme.colors.textPrimary }}>
                      Enable Automatic Call Distribution (ACD)
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                      Ring Strategy
                    </label>
                    <select
                      name="ring_strategy"
                      defaultValue={systemConfig?.ring_strategy || 'round_robin'}
                      className="w-full px-3 py-2 rounded border"
                      style={{
                        backgroundColor: theme.colors.bgPage,
                        borderColor: theme.colors.borderColor,
                        color: theme.colors.textPrimary
                      }}
                    >
                      <option value="round_robin">Round Robin</option>
                      <option value="least_recent">Least Recent</option>
                      <option value="skill_based">Skill Based</option>
                      <option value="priority">Priority</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                      Max Ring Time (seconds)
                    </label>
                    <input
                      type="number"
                      name="max_ring_time"
                      min="10"
                      max="120"
                      defaultValue={systemConfig?.max_ring_time || 30}
                      className="w-32 px-3 py-2 rounded border"
                      style={{
                        backgroundColor: theme.colors.bgPage,
                        borderColor: theme.colors.borderColor,
                        color: theme.colors.textPrimary
                      }}
                    />
                  </div>
                </div>

                <div className="space-y-4 p-4 rounded" style={{ backgroundColor: theme.colors.bgHover }}>
                  <h3 className="font-semibold" style={{ color: theme.colors.textPrimary }}>Feature Toggles</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="voicemail_enabled"
                        name="voicemail_enabled"
                        defaultChecked={systemConfig?.voicemail_enabled}
                        className="mr-2"
                      />
                      <label htmlFor="voicemail_enabled" style={{ color: theme.colors.textPrimary }}>
                        Voicemail
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="callback_enabled"
                        name="callback_enabled"
                        defaultChecked={systemConfig?.callback_enabled}
                        className="mr-2"
                      />
                      <label htmlFor="callback_enabled" style={{ color: theme.colors.textPrimary }}>
                        Callback
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="quality_monitoring"
                        name="quality_monitoring"
                        defaultChecked={systemConfig?.quality_monitoring}
                        className="mr-2"
                      />
                      <label htmlFor="quality_monitoring" style={{ color: theme.colors.textPrimary }}>
                        Quality Monitoring
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="ai_assistance"
                        name="ai_assistance"
                        defaultChecked={systemConfig?.ai_assistance}
                        className="mr-2"
                      />
                      <label htmlFor="ai_assistance" style={{ color: theme.colors.textPrimary }}>
                        AI Assistance
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="real_time_transcription"
                        name="real_time_transcription"
                        defaultChecked={systemConfig?.real_time_transcription}
                        className="mr-2"
                      />
                      <label htmlFor="real_time_transcription" style={{ color: theme.colors.textPrimary }}>
                        Real-time Transcription
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="sentiment_analysis"
                        name="sentiment_analysis"
                        defaultChecked={systemConfig?.sentiment_analysis}
                        className="mr-2"
                      />
                      <label htmlFor="sentiment_analysis" style={{ color: theme.colors.textPrimary }}>
                        Sentiment Analysis
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={updateSystemConfig.isPending}
                    className="px-6 py-2 rounded font-medium"
                    style={{
                      backgroundColor: theme.colors.primaryColor,
                      color: '#ffffff'
                    }}
                  >
                    {updateSystemConfig.isPending ? 'Saving...' : 'Save Configuration'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </Card>
    </AdminPageLayout>
  );
}
